'use client';

import { c as _c } from "react/compiler-runtime";
import type { TFunction } from 'i18next';
import { createSerializer } from 'nuqs';
import { useState } from 'react';
import { throttle } from 'throttle-debounce';
import { generalConfig } from '@sola/config/lib/getters.ts';
import { countryIsoToRegionIso, programCountryIsoCodes } from '@sola/contentful-model/lib/sac-values.ts';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import type { ChildSearchEvent } from '@sola/tracking/lib/analytics/events.ts';
import { generateDayItems, generateMonthItems } from '@sola/utils/date.ts';
import type { SacQueryParams } from '../../utils/query-params.sac.tsx';
import { SacFiltersEnum, sacFilterAll, sacQueryParamsSchema } from '../../utils/query-params.sac.tsx';
import { resolveChipFilters } from '../sac-list/sac-list.logic.ts';
const {
  sacMinAge,
  sacMaxAge
} = generalConfig;
type SacFilterChipValue = SacFiltersEnum | typeof sacFilterAll;
type SacFilterChipItem = {
  name: string;
  value: SacFilterChipValue;
  icon?: IconNames;
  isClear?: boolean;
  isSelected?: boolean;
};
export const buildGenderOptions = (t: TFunction<['children', 'core', 'geo']>) => [{
  value: ChildGenderEnum.Male,
  text: t('children:sac.controls.boy')
}, {
  value: ChildGenderEnum.Female,
  text: t('children:sac.controls.girl')
}, {
  value: ChildGenderEnum.Either,
  text: t('children:sac.controls.either')
}];
export const buildCountryOptions = (t: TFunction<['children', 'core', 'geo']>, isMultipleCountry: boolean | null | undefined) => [{
  value: '',
  text: t('children:sac.controls.allCountries')
}, ...(isMultipleCountry ? [{
  value: 'multiple',
  text: t('children:sac.controls.multipleCountries'),
  disabled: true
}] : []), ...programCountryIsoCodes.map(c => ({
  value: c,
  text: t(`geo:countries.${c}.nameS` as 'geo:countries.BD.nameS')
})).toSorted((a, b) => a.text.localeCompare(b.text))];
export const buildMonthOptions = (t: TFunction<['children', 'core', 'geo']>) => [{
  value: '0',
  text: t('children:sac.controls.month')
}, ...generateMonthItems()];
export const buildDayOptions = (t: TFunction<['children', 'core', 'geo']>) => [{
  value: '0',
  text: t('children:sac.controls.day')
}, ...generateDayItems()];
export const buildChipItems = (t: TFunction<['children', 'core', 'geo']>, selectedFilters?: SacFiltersEnum[]): SacFilterChipItem[] => [{
  name: t('children:sac.all'),
  value: sacFilterAll,
  isClear: true,
  isSelected: !selectedFilters?.length
}, {
  name: t('children:sac.orphanedChild'),
  value: SacFiltersEnum.enum.orphanedChild,
  icon: 'child' as IconNames
}, {
  name: t('children:sac.hasDisabilities'),
  value: SacFiltersEnum.enum.hasDisabilities,
  icon: 'wheelchair' as IconNames
}, {
  name: t('children:sac.vulnerableToExploitation'),
  value: SacFiltersEnum.enum.vulnerableExploitation,
  icon: 'alert-triangle' as IconNames
}, {
  name: t('children:sac.affectedByHiv'),
  value: SacFiltersEnum.enum.affectedHiv,
  icon: 'virus' as IconNames
}];
export const buildTrackingEvent = (sacFilters?: SacQueryParams): ChildSearchEvent => {
  const chips = resolveChipFilters(sacFilters?.filters);
  return {
    gender: sacFilters?.gender || undefined,
    ageMin: sacFilters?.ageMin || undefined,
    ageMax: sacFilters?.ageMax || undefined,
    region: countryIsoToRegionIso(sacFilters?.countries?.[0] || ''),
    country: [...(sacFilters?.countries || [])],
    birthMonth: sacFilters?.birthMonth || undefined,
    birthDay: sacFilters?.birthDay || undefined,
    orphan: chips.isOrphan,
    disability: chips.disability,
    highlyVulnerable: chips.isVulnerable,
    hiv: chips.isInHivAffectedArea
  };
};
const serializeSacFilters = createSerializer(sacQueryParamsSchema);
export const getSerializedSacUrl = (sacFilters?: SacQueryParams) => `/sponsor-a-child${serializeSacFilters(sacFilters ?? {})}`;
export const hasAdvancedSacCriteria = (sacFilters?: SacQueryParams) => !!(sacFilters?.gender && sacFilters.gender !== ChildGenderEnum.Either || sacFilters?.ageMin !== undefined && sacFilters.ageMin !== sacMinAge || sacFilters?.ageMax !== undefined && sacFilters.ageMax !== sacMaxAge || (sacFilters?.countries?.filter(Boolean).length ?? 0) > 0);
export const useSacControlsState = (sacFilters, setSacFilters) => {
  const $ = _c(30);
  let t0;
  if ($[0] !== sacFilters) {
    t0 = hasAdvancedSacCriteria(sacFilters);
    $[0] = sacFilters;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [showAdvanced, setShowAdvanced] = useState(t0);
  const t1 = sacFilters?.ageMin ?? sacMinAge;
  const t2 = sacFilters?.ageMax ?? sacMaxAge;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = [t1, t2];
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const ageRange = t3;
  const [isMultipleCountry, setIsMultipleCountry] = useState(sacFilters?.countries && sacFilters.countries.length > 1);
  let t4;
  if ($[5] !== setSacFilters) {
    t4 = throttle(300, (min, max) => {
      setSacFilters?.({
        ageMin: min,
        ageMax: max
      });
    }, {
      noLeading: true
    });
    $[5] = setSacFilters;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const throttledSliderCommit = t4;
  let t5;
  if ($[7] !== setSacFilters) {
    t5 = v => {
      setSacFilters?.({
        gender: v?.target?.value as ChildGenderEnum
      });
    };
    $[7] = setSacFilters;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleGenderChange = t5;
  let t6;
  if ($[9] !== throttledSliderCommit) {
    t6 = t7 => {
      const {
        newValue
      } = t7;
      if (newValue?.length === 2) {
        const [min_0, max_0] = newValue;
        throttledSliderCommit(min_0, max_0);
      }
    };
    $[9] = throttledSliderCommit;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleAgeSliderCommit = t6;
  let t7;
  if ($[11] !== setSacFilters) {
    t7 = v_0 => {
      setIsMultipleCountry(false);
      setSacFilters?.({
        countries: [v_0?.target?.value].filter(Boolean)
      });
    };
    $[11] = setSacFilters;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleCountryChange = t7;
  let t8;
  if ($[13] !== setSacFilters) {
    t8 = v_1 => {
      setSacFilters?.({
        birthMonth: Number(v_1?.target?.value)
      });
    };
    $[13] = setSacFilters;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleBirthMonthChange = t8;
  let t9;
  if ($[15] !== setSacFilters) {
    t9 = v_2 => {
      setSacFilters?.({
        birthDay: Number(v_2?.target?.value)
      });
    };
    $[15] = setSacFilters;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const handleBirthDayChange = t9;
  let t10;
  if ($[17] !== setSacFilters) {
    t10 = v_3 => {
      setSacFilters?.({
        filters: v_3.filter(_temp)
      });
    };
    $[17] = setSacFilters;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const handleChipChange = t10;
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setShowAdvanced(_temp2);
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const toggleAdvanced = t11;
  let t12;
  if ($[20] !== ageRange || $[21] !== handleAgeSliderCommit || $[22] !== handleBirthDayChange || $[23] !== handleBirthMonthChange || $[24] !== handleChipChange || $[25] !== handleCountryChange || $[26] !== handleGenderChange || $[27] !== isMultipleCountry || $[28] !== showAdvanced) {
    t12 = {
      showAdvanced,
      ageRange,
      isMultipleCountry,
      sacMinAge,
      sacMaxAge,
      handleGenderChange,
      handleAgeSliderCommit,
      handleCountryChange,
      handleBirthMonthChange,
      handleBirthDayChange,
      handleChipChange,
      toggleAdvanced
    };
    $[20] = ageRange;
    $[21] = handleAgeSliderCommit;
    $[22] = handleBirthDayChange;
    $[23] = handleBirthMonthChange;
    $[24] = handleChipChange;
    $[25] = handleCountryChange;
    $[26] = handleGenderChange;
    $[27] = isMultipleCountry;
    $[28] = showAdvanced;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
};
function _temp(x) {
  return SacFiltersEnum.safeParse(x).success;
}
function _temp2(v_4) {
  return !v_4;
}